import React, { useMemo } from 'react';
import s from './styles.module.scss';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { ITranslation } from '@/infrastructure/interfaces/i-translation';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Translation } from '@/infrastructure/class/translation';
import { sortArrayByFieldName } from '@/utils/helpers/sort-array-by-field-name';
import Image from 'next/image';
import { DEFAULT_TRANSLATION } from '@/infrastructure/const/default-translation';

interface ITranslationsSelectorProps {
  items: ITranslation[];
  activeTranslation: string;
}

const TranslationsSelector: React.FC<ITranslationsSelectorProps> = (props) => {
  const { items, activeTranslation } = props;

  const router = useRouter();
  const { locale } = router;

  const { t: tButtons } = useTranslation('buttons');

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const translations = useMemo<Translation[]>(
    () =>
      sortArrayByFieldName(
        items.map((translation) => new Translation(translation, locale)),
        'language'
      ),
    [items, locale]
  );

  const currentTranslation = useMemo<Translation | undefined>(() => {
    const lang = activeTranslation || DEFAULT_TRANSLATION;
    return translations.find((translation) => translation.slug === lang);
  }, [activeTranslation, translations]);

  const getFlagImage = (language: string) => {
    return `/flags/${language.toLowerCase()}.svg`;
  };

  const onSelect = (translation: Translation) => {
    router.query.translation = translation.slug;
    router.push(router, undefined, { scroll: false });
    handleClose();
  };

  return (
    <div>
      <IconButton
        onClick={handleClick}
        aria-label={tButtons('selectTranslation')}
        title={tButtons('selectTranslation')}
      >
        {currentTranslation && (
          <div className={s['flag']}>
            <Image
              className={s['flag']}
              alt="selected-language"
              src={getFlagImage(currentTranslation.language)}
              width={35}
              height={35}
              fetchPriority="high"
            />
          </div>
        )}
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {translations.map((translation) => (
          <MenuItem
            key={translation.slug}
            selected={currentTranslation?.slug === translation.slug}
            onClick={() => onSelect(translation)}
          >
            <div>
              <i className={s['flag']}>
                <Image
                  className={s['flag']}
                  alt={translation.language}
                  src={getFlagImage(translation.language)}
                  width={35}
                  height={35}
                  fetchPriority="high"
                />
              </i>
            </div>
            <span> {translation.titleEn}</span>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default TranslationsSelector;
